import {
  getNextAvailableDate,
  isInAnyDateRange,
  lookupCategoryActiveDatesEntry
} from '@/utils/dateRanges';

const getDefaultState = () => ({
  grid: [],
  gridSize: 0,
  boardMode: 'places',
  label: '',
  bingosAchieved: 0,
  isBlackout: false,
  prizes: [],
  multiWin: false,
  pendingBingoCell: null,
  lastCheckInResult: null,
  categoryActiveDates: {},
  categoryDisplayMode: 'text',
  categoryImageUrls: {},
  bingoPrizes: [],
  placeIconUrl: null
});

const state = getDefaultState();

const getters = {
  hasBingo: state => !!state.grid?.length,
  markedCount: state => {
    if (!state.grid?.length) return 0;
    let count = 0;
    state.grid.forEach(row => {
      row.forEach(cell => {
        if (cell.type !== 'free' && cell.marked) count += 1;
      });
    });
    return count;
  },
  totalCells: state => {
    if (!state.grid?.length) return 0;
    let total = 0;
    state.grid.forEach(row => {
      row.forEach(cell => {
        if (cell.type !== 'free') total += 1;
      });
    });
    return total;
  },
  isCenter: (state, getters) => (row, col) => {
    const size = state.gridSize || getters.gridSizeFromGrid;
    const center = Math.floor(size / 2);
    return row === center && col === center;
  },
  gridSizeFromGrid: state =>
    state.grid?.length ? state.grid.length : 0,
  claimablePrizes: state =>
    state.prizes?.filter(p => p.claimed_at == null) || [],
  pendingBingoCell: state => state.pendingBingoCell,
  getCellForVenueId: state => venueId => {
    if (!state.grid?.length || venueId == null) return null;
    const target = Number(venueId);
    // Auto-mark must skip cells already marked (idempotent re-tap of a venue
    // would otherwise resolve to the same marked cell and the backend would
    // reject the request). For category mode also skip venues already
    // consumed by another marked cell so the next tap rolls onto a fresh
    // category cell instead of looping on the same one.
    const usedVenueIds = new Set();
    for (const rowData of state.grid) {
      for (const cell of rowData) {
        if (cell?.marked && cell.marked_by_venue_id != null)
          usedVenueIds.add(Number(cell.marked_by_venue_id));
      }
    }
    for (let row = 0; row < state.grid.length; row++) {
      const rowData = state.grid[row];
      for (let col = 0; col < rowData.length; col++) {
        const cell = rowData[col];
        if (!cell || cell.marked) continue;
        if (cell.type === 'venue' && Number(cell.venue_id) === target)
          return [row, col];
        if (
          cell.type === 'category' &&
          Array.isArray(cell.category_venue_ids) &&
          cell.category_venue_ids.some(id => Number(id) === target) &&
          !usedVenueIds.has(target)
        )
          return [row, col];
      }
    }
    return null;
  },
  bingoRewardPrize: state =>
    state.prizes?.find(p => p.type === 'first_bingo') || null,
  blackoutPrize: state =>
    state.prizes?.find(p => p.type === 'blackout') || null,
  hasBlackoutPrize: state => {
    const prize = state.prizes?.find(p => p.type === 'blackout');
    return prize?.product_id != null;
  },
  latestWonBingoNumbers: state => {
    const newBingos = state.lastCheckInResult?.new_bingos ?? 0;
    if (newBingos <= 0) return [];
    const total = state.bingosAchieved;
    const lowest = total - newBingos + 1;
    const numbers = [];
    for (let n = lowest; n <= total; n++) numbers.push(n);
    return numbers;
  },
  // Prize entries to display in the bingo win dialog, keyed off the latest
  // won bingo numbers. Skips rungs without a configured prize so the dialog
  // never shows a placeholder "Prize" entry for an unconfigured ladder slot.
  // Centralized here so Tickets and Bingo views share the same payload shape.
  latestWonPrizesForDialog: (state, getters, _rootState, rootGetters) => {
    const numbers = getters.latestWonBingoNumbers;
    if (!numbers.length) return [];
    const ladder = Array.isArray(state.bingoPrizes) ? state.bingoPrizes : [];
    const byNumber = new Map(
      ladder
        .map(p => [Number(p.bingo_number), p.product_id ?? null])
        .filter(([n]) => Number.isFinite(n) && n > 0)
    );
    return numbers
      .filter(n => byNumber.has(n) && byNumber.get(n) != null)
      .map(n => {
        const productId = byNumber.get(n);
        const product = rootGetters['products/getById']?.(productId);
        return { bingo_number: n, name: product?.name || 'Prize' };
      });
  },
  getCategoryAvailability: state => categoryId => {
    const base = (isAvailable, isBlackout, nextAvailableDate) => ({
      isAvailable,
      isBlackout,
      nextAvailableDate
    });
    if (state.boardMode !== 'categories') {
      return base(true, false, null);
    }
    const entry = lookupCategoryActiveDatesEntry(
      state.categoryActiveDates,
      categoryId
    );
    if (entry == null) {
      return base(true, false, null);
    }
    const now = new Date();
    const activeDates = entry.active_dates || [];
    const blackoutDates = entry.blackout_dates || [];

    if (isInAnyDateRange(now, blackoutDates)) {
      return base(
        false,
        true,
        getNextAvailableDate(activeDates, blackoutDates, now)
      );
    }

    if (activeDates.length > 0) {
      if (!isInAnyDateRange(now, activeDates)) {
        return base(
          false,
          false,
          getNextAvailableDate(activeDates, blackoutDates, now)
        );
      }
    }

    return base(true, false, null);
  },
  usedVenueIds: state => {
    if (!state.grid?.length) return [];
    const ids = new Set();
    state.grid.forEach(row => {
      row.forEach(cell => {
        if (cell?.marked && cell.marked_by_venue_id != null) {
          ids.add(Number(cell.marked_by_venue_id));
        }
      });
    });
    return Array.from(ids);
  }
};

const mutations = {
  SET_BINGO_STATE(state, payload) {
    if (!payload) return;
    state.grid = payload.grid || [];
    state.gridSize = payload.grid_size ?? payload.gridSize ?? 0;
    state.boardMode = payload.board_mode ?? payload.boardMode ?? 'places';
    state.label = payload.label ?? '';
    state.bingosAchieved = payload.bingos_achieved ?? payload.bingosAchieved ?? 0;
    state.isBlackout = payload.is_blackout ?? payload.isBlackout ?? false;
    state.prizes = payload.prizes || [];
    state.multiWin = payload.multi_win ?? payload.multiWin ?? false;
    state.categoryActiveDates =
      payload.category_active_dates ?? payload.categoryActiveDates ?? {};
    state.categoryDisplayMode =
      payload.category_display_mode ?? payload.categoryDisplayMode ?? 'text';
    state.categoryImageUrls =
      payload.category_image_urls ?? payload.categoryImageUrls ?? {};
    state.bingoPrizes =
      payload.bingo_prizes ?? payload.bingoPrizes ?? [];
    state.placeIconUrl =
      payload.place_icon_url ?? payload.placeIconUrl ?? null;
  },
  MARK_CELL(state, { row, col, marked, markedByVenueId, orderProductId }) {
    if (!state.grid[row]?.[col]) return;
    const cell = state.grid[row][col];
    cell.marked = marked;
    if (marked) {
      if (markedByVenueId != null) cell.marked_by_venue_id = markedByVenueId;
      if (orderProductId != null) cell.order_product_id = orderProductId;
    }
  },
  SET_BINGO_COUNT(state, count) {
    state.bingosAchieved = count;
  },
  SET_BLACKOUT(state, value) {
    state.isBlackout = !!value;
  },
  SET_PENDING_BINGO_CELL(state, value) {
    state.pendingBingoCell = value;
  },
  SET_LAST_CHECK_IN_RESULT(state, value) {
    state.lastCheckInResult = value;
  },
  RESET_BINGO(state) {
    Object.assign(state, getDefaultState());
  }
};

const actions = {
  addBingo({ commit }, payload) {
    if (!payload || !payload.grid) return;
    commit('SET_BINGO_STATE', payload);
  },
  setBingoState({ commit }, payload) {
    commit('SET_BINGO_STATE', payload);
  },
  markCell({ commit }, { row, col, ...rest }) {
    commit('MARK_CELL', { row, col, marked: true, ...rest });
  },
  setBingoCount({ commit }, count) {
    commit('SET_BINGO_COUNT', count);
  },
  setBlackout({ commit }, value) {
    commit('SET_BLACKOUT', value);
  },
  setPendingBingoCell({ commit }, value) {
    commit('SET_PENDING_BINGO_CELL', value);
  },
  handleCheckInResult({ commit, state }, result) {
    if (!result) return;
    commit('SET_LAST_CHECK_IN_RESULT', result);
    const pending = state.pendingBingoCell;
    const row = result.row ?? (Array.isArray(pending) ? pending[0] : pending?.row);
    const col = result.col ?? (Array.isArray(pending) ? pending[1] : pending?.col);
    if (row != null && col != null && result.marked) {
      commit('MARK_CELL', {
        row,
        col,
        marked: true,
        markedByVenueId: result.marked_by_venue_id,
        orderProductId: result.order_product_id
      });
    }
    if (result.new_bingos != null && result.new_bingos > 0) {
      commit('SET_BINGO_COUNT', state.bingosAchieved + result.new_bingos);
    }
    if (result.is_blackout) {
      commit('SET_BLACKOUT', true);
    }
    commit('SET_PENDING_BINGO_CELL', null);
  },
  clearPendingBingoCell({ commit }) {
    commit('SET_PENDING_BINGO_CELL', null);
  },
  clearLastCheckInResult({ commit }) {
    commit('SET_LAST_CHECK_IN_RESULT', null);
  },
  resetBingo({ commit }) {
    commit('RESET_BINGO');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
